import { type EnvironmentId } from "@cmmd-forge/contracts";
import {
  listPullRequestEligibleSavedEnvironmentRecords,
  type SavedEnvironmentRecord,
  waitForSavedEnvironmentRegistryHydration,
} from "./catalog";
import { type EnvironmentConnection } from "./connection";

export type PullRequestLeaseDependencies = {
  readonly readConnection: (environmentId: EnvironmentId) => EnvironmentConnection | null;
  readonly ensureCmmdRuntimeSavedEnvironmentConnection: (
    record: SavedEnvironmentRecord,
  ) => Promise<EnvironmentConnection>;
};

const pullRequestEnvironmentLeases = new Map<EnvironmentId, number>();

export function resetPullRequestEnvironmentLeasesForTests(): void {
  pullRequestEnvironmentLeases.clear();
}

export type PullRequestEnvironmentLease = () => void;

function releasePullRequestEnvironmentLease(environmentIds: ReadonlyArray<EnvironmentId>): void {
  for (const environmentId of environmentIds) {
    const refCount = pullRequestEnvironmentLeases.get(environmentId);
    if (refCount === undefined || refCount <= 1) {
      pullRequestEnvironmentLeases.delete(environmentId);
    } else {
      pullRequestEnvironmentLeases.set(environmentId, refCount - 1);
    }
  }
}

async function acquirePullRequestEnvironment(
  record: SavedEnvironmentRecord,
  dependencies: PullRequestLeaseDependencies,
): Promise<boolean> {
  const environmentId = record.environmentId;
  pullRequestEnvironmentLeases.set(
    environmentId,
    (pullRequestEnvironmentLeases.get(environmentId) ?? 0) + 1,
  );

  try {
    if (dependencies.readConnection(environmentId)) {
      return true;
    }

    await dependencies.ensureCmmdRuntimeSavedEnvironmentConnection(record);
    return true;
  } catch {
    releasePullRequestEnvironmentLease([environmentId]);
    return false;
  }
}

/**
 * Retain trusted PR-capable saved environments for a route consumer.
 *
 * The saved-environment service owns live connections beyond a route lifetime.
 * Releasing a PR lease therefore cannot disconnect a thread that happened to
 * reuse the same connection; the refcount exists to coalesce route consumers.
 */
export async function acquirePullRequestEnvironmentLeaseConnections(
  dependencies: PullRequestLeaseDependencies,
): Promise<PullRequestEnvironmentLease> {
  await waitForSavedEnvironmentRegistryHydration();
  const acquiredEnvironmentIds = (
    await Promise.all(
      listPullRequestEligibleSavedEnvironmentRecords().map(async (record) =>
        (await acquirePullRequestEnvironment(record, dependencies)) ? record.environmentId : null,
      ),
    )
  ).flatMap((environmentId) => (environmentId === null ? [] : [environmentId]));

  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    releasePullRequestEnvironmentLease(acquiredEnvironmentIds);
  };
}
